import { createRouter, createWebHistory } from "vue-router";
import store from "@/store/index";
import { modalService } from "@/services/modalService";

// 서브도메인: 페이지 로드 시점에 고정되므로 모듈 스코프에서 분기해 해당 라우트의 레이아웃/컴포넌트를 서브도메인용 디자인으로 스왑한다.
const currentSubdomain = store.getters["appStore/subdomain"];
const bySubdomain = (subdomainLoaders, defaultLoader) =>
  subdomainLoaders[currentSubdomain] || defaultLoader;
const isAllowedSubdomainRoute = (route, isSubdomain) => {
  const allowed = route.meta.subdomainOnly;
  if (allowed) {
    return Array.isArray(allowed)
      ? allowed.includes(currentSubdomain)
      : allowed === currentSubdomain;
  }
  if (route.meta.subdomainRequired) return isSubdomain;
  return true;
};

const routes = [
  // Header만 (Footer 없음 - MainPage는 내부에 footer 포함)
  {
    path: "/",
    component: bySubdomain(
      { ctp: () => import("@/layouts/CtpLayout.vue") },
      () => import("@/layouts/MainLayout.vue")
    ),
    children: [
      {
        path: "",
        name: "HomePage",
        component: bySubdomain(
          { ctp: () => import("@/views/ctp/CtpHomePage.vue") },
          () => import("@/views/MainPage.vue")
        ),
      },
    ],
  },

  // Header + Footer
  {
    path: "/",
    component: bySubdomain(
      { ctp: () => import("@/layouts/CtpLayout.vue") },
      () => import("@/layouts/DefaultLayout.vue")
    ),
    children: [
      // 유저
      {
        path: "/login",
        name: "Login",
        component: () => import("@/views/us/LoginPage.vue"),
        meta: { guestOnly: true },
      },
      {
        path: "/sso",
        component: () => import("@/views/us/SsoPage.vue"),
      },
      {
        path: "/sso/cmp-register",
        component: () => import("@/views/us/SsoCmpRegisterPage.vue"),
      },
      {
        path: "/sign-up",
        component: () => import("@/views/us/SignUpPage.vue"),
        meta: { guestOnly: true },
      },
      {
        path: "/sign-up/fail",
        component: () => import("@/views/us/SignUpFailPage.vue"),
      },
      {
        path: "/sign-up/complete",
        name: "SignUpComplete",
        props: true,
        component: () => import("@/views/us/SignUpCompletePage.vue"),
      },
      {
        path: "/find-id",
        component: () => import("@/views/us/FindIdPage.vue"),
        meta: { guestOnly: true },
      },
      {
        path: "/find-pwd",
        component: () => import("@/views/us/FindPwdPage.vue"),
        meta: { guestOnly: true },
      },

      // 빅데이터 발굴
      {
        path: "/bm",
        component: () => import("@/views/bm/BmPage.vue"),
      },
      {
        path: "/bm/big-data",
        component: bySubdomain(
          { ctp: () => import("@/views/ctp/CtpBigDataPage.vue") },
          () => import("@/views/bm/BigDataPage.vue")
        ),
        meta: { authRequired: true },
      },

      // 마케팅 발송
      {
        path: "/bm/history",
        component: bySubdomain(
          { ctp: () => import("@/views/ctp/CtpBmHistoryPage.vue") },
          () => import("@/views/bm/BmPushHistoryPage.vue")
        ),
        meta: { authRequired: true },
      },
      {
        path: "/bm/push",
        name: "BmPush",
        component: bySubdomain(
          { ctp: () => import("@/views/ctp/CtpBmPushPage.vue") },
          () => import("@/views/bm/BmPushPage.vue")
        ),
        meta: { authRequired: true },
      },
      {
        name: "BmPushComplete",
        path: "/bm/push-complete/:mktNo",
        component: () => import("@/views/bm/BmSendCompletePage.vue"),
        props: true,
        meta: { authRequired: true },
      },
      {
        path: "/bm/tmp",
        name: "BmTmp",
        component: bySubdomain(
          { ctp: () => import("@/views/ctp/CtpBmTmpPage.vue") },
          () => import("@/views/bm/BmTmpPage.vue")
        ),
        meta: { authRequired: true },
      },

      // 사업 참가신청 (ctp 서브도메인 전용)
      {
        path: "/ctp-apply",
        name: "CtpApply",
        component: () => import("@/views/ctp/apply/CtpApplyPage.vue"),
        meta: {
          subdomainOnly: "ctp",
          authRequired: true
        },
      },
      {
        path: "/ctp-apply/history",
        name: "CtpApplyHistory",
        component: () => import("@/views/ctp/apply/CtpApplyHistoryPage.vue"),
        meta: {
          subdomainOnly: "ctp",
          authRequired: true
        },
      },
      {
        path: "/ctp-apply/detail",
        name: "CtpApplyDetail",
        component: () => import("@/views/ctp/apply/CtpApplyDetailPage.vue"),
        meta: {
          subdomainOnly: "ctp",
          authRequired: true
        },
      },

      // 문의, 공지
      {
        name: "CsNotices",
        path: "/cs-notices",
        component: () => import("@/views/cs/CsNoticePage.vue"),
      },
      {
        name: "CsFaq",
        path: "/cs-faq",
        component: () => import("@/views/cs/CsFaqPage.vue"),
      },
      {
        path: "/cs-contacts",
        component: () => import("@/views/cs/CsContactPage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/cs-contact-complete",
        component: () => import("@/views/cs/CsContactCompletePage.vue"),
        meta: { authRequired: true },
      },

      // 마이페이지
      {
        name: "MyPage",
        path: "/mypage",
        component: () => import("@/views/my/MyPage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/update-info",
        component: () => import("@/views/my/UpdateInfoPage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/cash-history",
        component: () => import("@/views/my/CashHistoryPage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/contact-history",
        component: () => import("@/views/my/ContactHistoryPage.vue"),
        meta: { authRequired: true },
      },

      // 컨설팅
      {
        path: "/apply-consults",
        component: () => import("@/views/consult/ApplyConsultPage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/apply-consult-complete",
        component: () => import("@/views/consult/ApplyConsultCompletePage.vue"),
        meta: { authRequired: true },
      },
      {
        path: "/consult-lists",
        component: () => import("@/views/consult/ConsultListPage.vue"),
        meta: { authRequired: true },
      },
      {
        name: "consultDetail",
        path: "/consult-detail/:consultNo",
        component: () => import("@/views/consult/ConsultDetailPage.vue"),
        meta: { authRequired: true },
      },
      {
        name: "FindScambuyers",
        path: "/find-scambuyers",
        component: () => import("@/views/buyer/FindScamBuyerPage.vue"),
      },

      // Footer 내 페이지
      {
        //이용약관
        name: "PolicyDetails",
        path: "/policy-details",
        component: () => import("@/views/common/PolicyDetailPage.vue"),
      },
      {
        //신용정보활용체제공시
        name: "CreditUsageNotice",
        path: "/credit-usage-notice",
        component: () => import("@/views/common/CreditUsageNoticePage.vue"),
      },
      {
        //이메일무단수집거부
        name: "EmailNoCollection",
        path: "/email-no-collection",
        component: () => import("@/views/common/EmailNoCollectionPage.vue"),
      },
    ],
  },

  // Header/Footer 없음 (팝업, 단독 페이지)
  {
    path: "/",
    component: () => import("@/layouts/BlankLayout.vue"),
    children: [
      {
        path: "/niceid/result",
        name: "NicePassResult",
        component: () => import("@/views/us/NicePassResultPage.vue"),
      },
      {
        path: "/bm/push-view/:mktNo",
        component: () => import("@/views/bm/BmSendMailViewPopup.vue"),
        props: true,
        name: "SendMailView",
        meta: { authRequired: true },
      },
      {
        path: "/bm/edm/:mktSndNo/:message?",
        component: () => import("@/views/bm/EdmSendPopup.vue"),
        props: true,
        name: "EdmSend",
      },
      {
        path: "/bm/edm-unsubscribe/:mktSndNo",
        component: () => import("@/views/bm/UnsubscribePage.vue"),
        props: true,
        name: "Unsubscribe",
      },
      {
        name: "BuyerMessageDetail",
        path: "/message/:dunsNo/:contactSeq",
        component: () => import("@/views/common/BuyerMessagePopup.vue"),
        props: true,
        meta: { authRequired: true },
      },
      {
        path: "/404",
        name: "NotFound",
        component: () => import("@/views/NotFoundPage.vue"),
      },
    ],
  },

  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

let guardId = 0;

router.beforeEach(async (to, from) => {
  // 메인(fullpage) 섹션 이동처럼 같은 경로에서 해시(#page)만 바뀌는 경우는 무시
  // (스크롤 톱 이동·가드·세션 연장 모두 건너뜀)
  if (to.path === from.path && to.hash !== from.hash) return;

  const myId = ++guardId;
  window.scrollTo({ top: 0, behavior: "smooth" });

  const isLogin = store.getters["userStore/isLogin"];
  const userGrade = store.getters["userStore/getUserAuthorityCode"];
  const isSubdomain = store.getters["appStore/isSubdomain"];

  // 비로그인시만 이용가능
  if (isLogin && to.matched.some((r) => r.meta.guestOnly)) {
    return "/";
  }

  // 서브도메인 전용 페이지 접근 제한
  if (to.matched.some((r) => !isAllowedSubdomainRoute(r, isSubdomain))) {
    return "/";
  }

  let alertMessage = "";
  let redirectPath = null;

  if (to.matched.some((r) => r.meta.managerPlatinum)) {
    if (userGrade * -1 < -1 || !isLogin) {
      alertMessage = "PLATINUM 등급 이상<br />접근 가능한 페이지입니다.";
      redirectPath = "/";
    }
  } else if (to.matched.some((r) => r.meta.managerBasic)) {
    if (userGrade * -1 < -2 || !isLogin) {
      alertMessage = "BASIC 등급 이상<br />접근 가능한 페이지입니다.";
      redirectPath = "/";
    }
  } else if (to.matched.some((r) => r.meta.managerFree)) {
    if (userGrade * -1 < -3 || !isLogin) {
      alertMessage = "FREE 등급 이상<br />접근 가능한 페이지입니다.";
      redirectPath = "/";
    }
  } else if (to.matched.some((r) => r.meta.authRequired)) {
    if (!isLogin) {
      alertMessage = "로그인 후 이용해 주세요.";
      // 로그인 후 원래 가려던 페이지로 복귀하도록 redirect 쿼리 부착
      redirectPath = {
        path: "/login",
        query: { redirect: encodeURIComponent(to.fullPath) },
      };
    }
  }

  if (alertMessage) {
    await modalService.alert({ title: "알림", content: alertMessage });
    // 모달 대기 중 새 네비게이션이 시작됐으면 이 guard는 무시
    if (myId !== guardId) return false;
    const targetPath =
      typeof redirectPath === "string" ? redirectPath : redirectPath.path;
    return from.path !== targetPath ? redirectPath : false;
  }
});

router.afterEach((to, from) => {
  // 메인 페이지 내 해시(#page) 이동은 세션 연장 대상에서 제외
  if (to.path === from.path && to.hash !== from.hash) return;

  const isLogin = store.getters["userStore/isLogin"];
  if (isLogin) {
    store.dispatch("userStore/LOGIN_EXTENSION", { skipLoading: true });
  }
});

export default router;
